/**
 * THE MOMENT PANEL.
 *
 * A fixed panel beside the boards that swaps to the current moment's photograph
 * as the race runs. The TV cutaway.
 *
 * ── IT IS ALLOWED TO BE EMPTY, AND THAT IS THE POINT ───────────────────────
 *
 * `pickImage` returns null whenever nothing honestly fits — a wet photo under a
 * dry race, a road-course shot at Talladega, a moment with no matching scene.
 * This panel shows nothing in that case rather than reaching for something
 * approximate. The engine is the truth and the picture is decoration; a wrong
 * picture is a lie the player can see, and it costs more than a blank panel.
 *
 * ── WHY IT DOES NOT CHANGE EVERY TURN ──────────────────────────────────────
 *
 * A fifty-turn race emits ~130 events. Swapping the picture on all of them
 * would be a strobe, so the panel picks the **most significant** moment of the
 * turn — a wreck outranks a pass outranks a box swap — and holds it until
 * something more significant arrives. This is the same principle the booth
 * works on (NARRATION_MODEL §8.1): silence between beats is part of the effect.
 *
 * ── DETERMINISM ────────────────────────────────────────────────────────────
 *
 * Selection is driven by the race seed, so scrubbing the ledger back to turn 34
 * shows the picture that was on screen at turn 34 — not a fresh random one.
 * Without that, UI-001's "run it back" would silently rewrite history every
 * time you used it.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { RuleEvent } from '../domain/ruleEvent.ts';
import { makeRng } from '../domain/dice.ts';
import { ArtHistory, momentSignificance, pickImage, type ArtPick } from '../domain/art/select.ts';
import { stillFor } from '../domain/booth/still.ts';
import type { Flag } from './boardModel.ts';
import type { Condition, Intensity, TrackCategory } from '../domain/art/scenes.ts';
import type { StoredArtImage } from '../lib/artStore.ts';
import { useCiteText } from '../lib/prefs.ts';
import { plainLine } from './story/plain.ts';

/**
 * Significance now lives in the domain (`select.ts`), because the rarity floors
 * are set against the same scale. Two copies of this table would drift and the
 * symptom would be a legendary photo firing on a routine pass.
 */
export const weigh = momentSignificance;

export interface MomentPanelProps {
  /** The whole library, already loaded. */
  library: readonly StoredArtImage[];
  /** Every event of the race, in order, each carrying its turn. */
  events: readonly (RuleEvent & { turn: number })[];
  /** The turn currently on screen. */
  cursor: number;
  trackCategory?: TrackCategory | string | null;
  condition?: Condition | null;
  /** The race seed, so the same race always shows the same pictures. */
  seed: number;
  /** Show the citation and scene under the image. Default on. */
  showMeta?: boolean;
  /**
   * A history carried ACROSS races. Without one, `maxPerSeason` cannot bind,
   * because a fresh history per race has no season memory to consult. Pass the
   * same object all season and a legendary photo stays legendary.
   */
  history?: ArtHistory;
  /**
   * Which flag is out. Chooses the booth still shown when there is no
   * photograph of the racing to show — see `booth/still.ts`.
   */
  flag?: Flag;
  /** Is the turn on screen a scheduled pit turn? Cuts the still to pit road. */
  onPitRoad?: boolean;
  /**
   * D-EZ-102 — turns that get the full-size picture (a late lead change, a
   * wreck, the finish — `story/raceStory.ts` `bigTurns`).
   */
  bigTurns?: ReadonlySet<number>;
}

interface Shown {
  turn: number;
  /** The picked image, narrowed back to the stored shape the library holds. */
  pick: ArtPick & { image: StoredArtImage };
  event: RuleEvent & { turn: number };
}

/**
 * Walk the race once and decide what is on screen at every turn.
 *
 * Done as a single pass rather than per-render because the no-repeat rule is
 * stateful: what turn 30 shows depends on what turns 1-29 already used. Picking
 * lazily as the cursor moves would give different answers depending on how the
 * user scrubbed, which is exactly the bug UI-001 was built to avoid.
 */
function buildTimeline(
  library: readonly StoredArtImage[],
  events: readonly (RuleEvent & { turn: number })[],
  trackCategory: TrackCategory | string | null | undefined,
  condition: Condition | null | undefined,
  seed: number,
  shared?: ArtHistory,
): Map<number, Shown> {
  const timeline = new Map<number, Shown>();
  if (library.length === 0 || events.length === 0) return timeline;

  const history = shared ?? new ArtHistory();
  history.startRace();
  const rng = makeRng(seed ^ 0x5eed);

  // Group by turn, then take the single most significant event in each.
  const byTurn = new Map<number, (RuleEvent & { turn: number })[]>();
  for (const e of events) {
    if (!e.momentTag) continue;
    const list = byTurn.get(e.turn) ?? [];
    list.push(e);
    byTurn.set(e.turn, list);
  }

  for (const turn of [...byTurn.keys()].sort((a, b) => a - b)) {
    const candidates = (byTurn.get(turn) ?? [])
      .slice()
      .sort((a, b) => weigh(b.momentTag) - weigh(a.momentTag));

    for (const event of candidates) {
      const intensity: Intensity =
        weigh(event.momentTag) >= 80 ? 'hot' : weigh(event.momentTag) <= 30 ? 'calm' : 'normal';
      const pick = pickImage(
        library,
        { momentTag: event.momentTag, trackCategory, condition, intensity, turn, significance: weigh(event.momentTag) },
        history,
        rng,
      );
      if (pick) {
        // `pickImage` is typed against the domain's ArtImage; the library this
        // panel is handed is StoredArtImage, which carries the pixel dimensions
        // used to reserve the aspect box.
        timeline.set(turn, { turn, pick: pick as ArtPick & { image: StoredArtImage }, event });
        break; // one picture per turn; the rest of the turn's events are not cut to
      }
    }
  }
  return timeline;
}

export function MomentPanel({
  library,
  events,
  cursor,
  trackCategory,
  condition,
  seed,
  showMeta = true,
  history,
  flag,
  onPitRoad,
  bigTurns,
}: MomentPanelProps) {
  const timeline = useMemo(
    () => buildTimeline(library, events, trackCategory, condition, seed, history),
    [library, events, trackCategory, condition, seed, history],
  );
  // D-EZ-102 — the caption in plain words; rule codes behind the citations switch.
  const cite = useCiteText();

  // Hold the most recent picture at or before the cursor, so quiet turns keep
  // the last image rather than blanking.
  const shown = useMemo(() => {
    let best: Shown | null = null;
    for (const [turn, s] of timeline) {
      if (turn <= cursor && (!best || turn > best.turn)) best = s;
    }
    return best;
  }, [timeline, cursor]);

  // A gentle cross-fade on change, and only on change — re-fading every render
  // would flicker on unrelated state updates.
  const [fade, setFade] = useState(false);
  const lastId = useRef<string | null>(null);
  useEffect(() => {
    const id = shown?.pick.image.id ?? null;
    if (id !== lastId.current) {
      lastId.current = id;
      setFade(true);
      const t = setTimeout(() => setFade(false), 260);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [shown]);

  /**
   * NO PICTURE OF THE RACING → CUT TO THE BOOTH.
   *
   * This used to be `return null` on an empty library and a grey "No picture for
   * this moment." the rest of the time — a third of the broadcast row saying
   * nothing for most of a race. A real director does not hold on black; he cuts
   * to the booth, and which shot he takes depends on whether the field is
   * actually racing. `stillFor` is that rule.
   */
  if (!shown) {
    return (
      <BoothStill
        flag={flag ?? 'green'}
        onPitRoad={onPitRoad ?? false}
        note={library.length === 0 ? null : 'No picture for this moment.'}
      />
    );
  }

  /*
    D-EZ-102 — THE BIG TURNS GET THE BIG PICTURE. A wreck, a late lead change
    or the finish is cut to full size; an ordinary pass keeps the inset. Read
    off the moment already on screen — never off a later turn.
  */
  const big = weigh(shown.event.momentTag) >= 80 || flag === 'checkered' || Boolean(bigTurns?.has(shown.turn));

  return (
    <aside className={`moment${big ? ' moment--big' : ''}`} aria-label="Moment">
      {false ? (
        <div className="moment__empty" />
      ) : (
        <figure className="moment__figure">
          <img
            key={shown.pick.image.id}
            src={shown.pick.image.url}
            alt=""
            className={`moment__img${fade ? ' is-fading' : ''}`}
            style={
              shown.pick.image.width && shown.pick.image.height
                ? { aspectRatio: `${shown.pick.image.width} / ${shown.pick.image.height}` }
                : undefined
            }
          />
          {showMeta && (
            <figcaption className="moment__meta">
              <span className="moment__turn">T{shown.turn}</span>
              <span className="moment__scene">{shown.pick.scene}</span>
              {shown.pick.rarity !== 'common' && (
                <span className={`moment__rarity moment__rarity--${shown.pick.rarity}`}>
                  {shown.pick.rarity}
                </span>
              )}
              {shown.pick.image.credit && <span className="dim moment__credit">{shown.pick.image.credit}</span>}
              <span className="dim moment__desc">{cite(shown.event.description, plainLine(shown.event.description))}</span>
            </figcaption>
          )}
        </figure>
      )}
    </aside>
  );
}

/**
 * THE BOOTH, WHEN THERE IS NO PICTURE OF THE RACING.
 *
 * Two frames, chosen by the flag: faces to camera when the field is not racing,
 * over the shoulder with the track behind them when it is. `booth/still.ts`
 * owns that rule and the reasoning behind it.
 *
 * A MISSING FILE RENDERS NOTHING. If the image will not load the whole panel
 * removes itself rather than leaving a broken frame with a caption under it —
 * "don't leave a blank with an explainer taped over it" applies to a picture as
 * much as to a number. Which also means the two files are genuinely optional:
 * without them the app behaves exactly as it did before.
 */
function BoothStill({
  flag,
  onPitRoad,
  note,
}: {
  flag: Flag;
  onPitRoad: boolean;
  note: string | null;
}) {
  const [broken, setBroken] = useState(false);
  const still = stillFor(flag, { onPitRoad });

  // A new shot deserves a fresh attempt — a file that failed once may simply
  // not have been deployed yet when the page loaded.
  useEffect(() => setBroken(false), [still.src]);

  if (broken) return null;

  return (
    <aside className={`moment moment--booth moment--${still.shot}`} aria-label="The booth">
      <figure className="moment__figure">
        <img
          src={still.src}
          alt={still.alt}
          className="moment__img moment__img--booth"
          onError={() => setBroken(true)}
        />
        <figcaption className="moment__meta">
          <span className="moment__scene">{still.caption}</span>
          {note && <span className="dim moment__note">{note}</span>}
        </figcaption>
      </figure>
    </aside>
  );
}
